import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { useAppStore } from "@/store";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { isWin } from "@/domains/platform/isWin";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSMenuContentItemKind, type MDSMenuContentItem, type MDSMenuContentList } from "@/mds";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import type { AppStoreAudioStore } from "@/store/audio/audio.store";
import { TrashRecordingReason } from "@/store/audio/audio.types";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import type {
  AudioCaptureActionsContextValue,
  AudioCaptureActionsProviderProps,
  AudioCaptureRefs,
  AudioCaptureUiState,
  AudioCaptureTranscriptState,
  AudioCaptureWaveformState,
} from "./audio-capture-actions.types";
import { createAudioCaptureSourceSnapshot } from "./audio-capture-actions.source";
import { RecallPermissionKind } from "@/modules/electron/types";
import { actions } from "@/actions";

export const useAudioCaptureTranscriptControls = ({
  transcript,
}: {
  transcript: AudioCaptureTranscriptState;
}) => {
  const [isTextTranscript, setIsTextTranscript] = useState(false);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const isAutoScrollEnabledRef = useRef(true);

  const handleTranscriptScroll = useDebounceCallback(() => {
    if (!transcriptContainerRef.current) return;
    const { scrollTop, clientHeight, scrollHeight } = transcriptContainerRef.current;
    const isCloseToBottom = scrollTop + clientHeight >= scrollHeight - 10;
    isAutoScrollEnabledRef.current = isCloseToBottom;
  }, 30);

  useLayoutEffect(() => {
    if (
      isAutoScrollEnabledRef.current &&
      transcript.hasTranscript &&
      transcriptContainerRef.current
    ) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcript.fullText, transcript.hasTranscript]);

  const toggleAdminMode = useCallback(() => {
    setIsTextTranscript(prev => !prev);
  }, []);

  return {
    isTextTranscript,
    toggleAdminMode,
    transcriptContainerRef,
    isAutoScrollEnabledRef,
    handleTranscriptScroll,
  };
};

export const useAudioCaptureWaveform = ({
  visualizer,
  phase,
  maxHistoryBars = 30,
}: {
  visualizer: AudioLevelProbe | null;
  phase: AudioCapturePhase;
  maxHistoryBars?: number;
}): AudioCaptureWaveformState => {
  const [historyBarHeights, setHistoryBarHeights] = useState<number[]>(
    Array.from({ length: maxHistoryBars }, () => 0.0)
  );

  useEffect(() => {
    if (!visualizer) return;

    const timeDomainData = new Uint8Array(visualizer.fftSize);
    let frameCounter = 0;
    let animationFrameId: number;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      frameCounter += 1;
      if (frameCounter % 12 !== 0) return;

      const isRecording = phase === AudioCapturePhase.Recording;

      visualizer.getByteTimeDomainData(timeDomainData);

      if (isRecording) {
        let rms = 0;
        for (let i = 0; i < timeDomainData.length; i += 1) {
          const sample = (timeDomainData[i] - 128) / 128.0;
          rms += sample * sample;
        }
        rms = Math.sqrt(rms / timeDomainData.length);

        let barHeight = Math.log10(rms * 65 + 1) / Math.log10(66);
        barHeight = Math.max(barHeight, 0.1);
        barHeight = Math.min(barHeight, 1);

        setHistoryBarHeights(prev => {
          const newHeights = [...prev, barHeight];
          if (newHeights.length > maxHistoryBars) {
            return newHeights.slice(-maxHistoryBars);
          }
          return newHeights;
        });
      }
    };

    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [phase, visualizer, maxHistoryBars]);

  return {
    bars: historyBarHeights,
    maxBars: maxHistoryBars,
  };
};

export const useAudioCaptureDeleteDialog = ({
  audioStore,
  recordingId,
  phase,
  onRequestClose,
}: {
  audioStore: AppStoreAudioStore;
  recordingId: string | null;
  phase: AudioCapturePhase;
  onRequestClose?: () => void;
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const close = useCallback(() => {
    setShowDeleteModal(false);
    onRequestClose?.();
  }, [onRequestClose]);

  const confirm = useCallback(() => {
    if (recordingId) {
      void audioStore.trashRecording({ audioRecordingId: recordingId });
    }
    close();
  }, [audioStore, close, recordingId]);

  const open = useCallback(async () => {
    if (phase === AudioCapturePhase.Recording && recordingId) {
      await audioStore.pauseRecording({ audioRecordingId: recordingId });
    }
    setShowDeleteModal(true);
  }, [audioStore, recordingId, phase]);

  return {
    showDeleteModal,
    open,
    close,
    confirm,
  };
};

const useAudioCaptureMenuItems = ({
  audioStore,
  isDesktop,
  onManageSystemAudioSettings,
  onOpenVoiceModeFaqs,
}: {
  audioStore: AppStoreAudioStore;
  isDesktop: boolean;
  onManageSystemAudioSettings: () => void;
  onOpenVoiceModeFaqs: () => void;
}): {
  dropdownItems: MDSMenuContentList;
  shouldShowDropdown: boolean;
} => {
  const dropdownItems = useMemo(() => {
    const items: MDSMenuContentItem[] = [
      {
        id: "copy-transcript",
        kind: MDSMenuContentItemKind.Item,
        label: "Copy Transcript",
        onClick: () => {
          void actions.copyRecordingTranscript({
            recording: audioStore.activeRecording,
          });
        },
      },
      {
        id: "download-audio",
        kind: MDSMenuContentItemKind.Item,
        label: "Download Audio",
        onClick: () =>
          void actions.downloadRecordingAudio({ recording: audioStore.activeRecording }),
      },
    ];

    if (isDesktop) {
      items.push({
        id: "separator",
        kind: MDSMenuContentItemKind.Divider,
      });
      items.push({
        id: "manage-system-audio",
        kind: MDSMenuContentItemKind.Item,
        label: "Manage System Audio Settings",
        onClick: onManageSystemAudioSettings,
      });

      items.push({
        id: "help-and-support",
        kind: MDSMenuContentItemKind.Item,
        label: "Voice Mode - FAQs",
        onClick: onOpenVoiceModeFaqs,
      });
    }

    return items;
  }, [audioStore.activeRecording, isDesktop, onManageSystemAudioSettings, onOpenVoiceModeFaqs]);

  return {
    dropdownItems,
    shouldShowDropdown: dropdownItems.length > 0,
  };
};

export const useAudioCaptureActionsContextValue = ({
  noteId,
  onRequestClose,
  adapters,
}: AudioCaptureActionsProviderProps): AudioCaptureActionsContextValue => {
  const { store: rootStore } = useAppStore();
  const store = adapters?.store ?? rootStore;
  const audioStore = store.audio;

  const sourceSnapshot = createAudioCaptureSourceSnapshot(store, { noteId });
  const {
    recording: recordingSnapshot,
    ui: uiSnapshot,
    environment,
    visualizer,
    recordingId,
  } = sourceSnapshot;
  const transcriptControls = useAudioCaptureTranscriptControls({
    transcript: recordingSnapshot.transcript,
  });
  const waveformState = useAudioCaptureWaveform({
    visualizer,
    phase: recordingSnapshot.phase,
  });
  const deleteDialog = useAudioCaptureDeleteDialog({
    audioStore,
    recordingId,
    phase: recordingSnapshot.phase,
    onRequestClose,
  });

  const handleManageSystemAudioSettings = useCallback(async () => {
    try {
      await electronModule.openSystemSoundSettings?.({ focus: "input" });
    } catch (error) {
      logger.warn({
        message: "[AudioCaptureActions] openSystemSoundSettings failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, []);

  const handleOpenVoiceModeFaqs = useCallback(() => {
    windowModule.openInNewTab({
      url: new URL(APP_CONFIG.URLS.FAQ_VOICE_MODE),
    });
  }, []);

  const { dropdownItems, shouldShowDropdown } = useAudioCaptureMenuItems({
    audioStore,
    isDesktop: environment.isDesktop,
    onManageSystemAudioSettings: handleManageSystemAudioSettings,
    onOpenVoiceModeFaqs: handleOpenVoiceModeFaqs,
  });

  const recordingState = {
    ...recordingSnapshot,
    waveform: waveformState,
  };

  const uiState: AudioCaptureUiState = {
    ...uiSnapshot,
    dropdownItems,
    shouldShowDropdown,
    showDeleteModal: deleteDialog.showDeleteModal,
    isTextTranscript: transcriptControls.isTextTranscript,
  };

  const refs: AudioCaptureRefs = {
    transcriptContainerRef: transcriptControls.transcriptContainerRef,
    isAutoScrollEnabledRef: transcriptControls.isAutoScrollEnabledRef,
  };

  const downloadUrl = useMemo(() => {
    const target = isWin() ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP;
    return new URL(target);
  }, []);

  return {
    noteId,
    recording: recordingState,
    ui: uiState,
    environment,
    refs,
    actions: {
      recording: {
        start: () => {
          void audioStore.createAndStartAudioRecording({ associatedNoteId: noteId });
        },
        pause: () => {
          if (!recordingId) return;
          return audioStore.pauseRecording({ audioRecordingId: recordingId });
        },
        resume: () => {
          if (!recordingId) return;
          return audioStore.resumeRecording({ audioRecordingId: recordingId });
        },
        finish: () => {
          if (!recordingId) return Promise.resolve();
          return audioStore.finishRecording({ audioRecordingId: recordingId });
        },
        retry: async () => {
          if (!recordingId) return;
          audioStore.pipeline.dispatchRetry({ audioRecordingId: recordingId });
        },
        recordAgain: async () => {
          if (!recordingId) return;
          await audioStore.trashRecording({
            audioRecordingId: recordingId,
            context: { reason: TrashRecordingReason.RecordAgain },
          });
          await audioStore.createAndStartAudioRecording({ associatedNoteId: noteId });
        },
        clear: () => {
          if (!recordingId) return;
          void audioStore.trashRecording({ audioRecordingId: recordingId });
        },
      },
      deleteDialog: {
        open: deleteDialog.open,
        close: deleteDialog.close,
        confirm: deleteDialog.confirm,
      },
      transcript: {
        toggleAdminMode: transcriptControls.toggleAdminMode,
        handleScroll: transcriptControls.handleTranscriptScroll,
      },
      systemAudio: {
        openSystemAudioSettings: handleManageSystemAudioSettings,
      },
      download: {
        openDesktopLink: () => {
          windowModule.openInNewTab({ url: downloadUrl });
        },
      },
      systemPermissions: {
        onOpenMicrophoneSystemPermissions: async () => {
          try {
            await electronModule.recall.openPermissionPane?.({
              permission: RecallPermissionKind.Microphone,
            });
          } catch (error) {
            logger.warn({
              message: "[AudioCaptureActions] onOpenMicrophoneSystemPermissions failed",
              info: { error: objectModule.safeErrorAsJson(error as Error) },
            });
          }
        },
      },
    },
    onRequestClose,
  } satisfies AudioCaptureActionsContextValue;
};
